import { useMutation, useQuery } from '@apollo/client';
import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useFeatureFlags } from '@procureai/permissions-ui';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GridRowClickAwayListener } from '../../components/GridRowClickAwayListener/GridRowClickAwayListener';
import { IntakeDataGrid } from '../../components/IntakeDataGrid/IntakeDataGrid';
import { useGridValidationStyling } from '../../components/IntakeDataGrid/IntakeDataGrid.hooks';
import { IntakeDrawer } from '../../components/IntakeDrawer/IntakeDrawer';
import { IntakeDrawerContent } from '../../components/IntakeDrawer/IntakeDrawerContent';
import { IntakeDrawerTitle } from '../../components/IntakeDrawer/IntakeDrawerTitle';
import {
  makeSupplierContactConfirmationArraySchema,
  makeSupplierContactConfirmationItemSchema,
} from '../../components/SupplierContactForm/SupplierContactForm.schema';
import {
  IntakeRequestSupplierSelectionGridField,
  useSupplierGridHandlers,
  useSupplierSelectionGridConfig,
} from '../../hooks/IntakeSupplierSelection.dataGrid';
import { FeatureFlag, I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { useChatSession } from '~/modules';
import { SupplierContactFormRoot } from '~/routes/IntakeDetail/components/SupplierContactFormRoot/SupplierContactFormRoot.tsx';

import { useNextMutation, useSupplierData } from './IntakeQuoteSupplierConfirmationStep.hooks';
import { INTAKE_CONFIRM_SUPPLIER_SELECTION_MUTATION } from './IntakeQuoteSupplierConfirmationStep.mutation.gql';
import { INTAKE_QUOTE_SUPPLIER_CONFIRMATION } from './IntakeQuoteSupplierConfirmationStep.query.gql';

export const IntakeQuoteSupplierConfirmationStep = ({ role, createdBy, chatSessionCompleted }: ChatMessageProps) => {
  const { t: tIntake } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeQuoteSupplierConfirmationStep' });
  const disabled = chatSessionCompleted;
  const session = useChatSession();

  const { hasFeatureFlag } = useFeatureFlags();
  const optionalContactLocation = hasFeatureFlag(FeatureFlag.OPTIONAL_QUOTED_SUPPLIER_CONTACT_LOCATION);
  const confirmationArraySchema = useMemo(
    () => makeSupplierContactConfirmationArraySchema(optionalContactLocation),
    [optionalContactLocation],
  );
  const confirmationItemSchema = useMemo(
    () => makeSupplierContactConfirmationItemSchema(optionalContactLocation),
    [optionalContactLocation],
  );

  const { data, loading: isLoading } = useQuery(INTAKE_QUOTE_SUPPLIER_CONFIRMATION, {
    variables: session,
  });

  const [save] = useMutation(INTAKE_CONFIRM_SUPPLIER_SELECTION_MUTATION, {
    refetchQueries: [{ query: INTAKE_QUOTE_SUPPLIER_CONFIRMATION, variables: session }],
  });

  const apiRef = useGridApiRef();
  const { quotedSupplier, otherSupplierIds } = useSupplierData(data);

  const { handleNext } = useNextMutation({ apiRef, session, next: save, isLoading, schema: confirmationArraySchema });

  const { getRowClassName, getGridClassName } = useGridValidationStyling(apiRef, confirmationArraySchema);

  const {
    currentSelectedRowId,
    handleAddRow,
    handleCloseDrawer,
    handleSupplierChange,
    handleRowDelete,
    hasSupplier,
    selectedSupplier,
    setRowSelectionModel,
    validate,
  } = useSupplierGridHandlers(apiRef, confirmationArraySchema);

  const config = useSupplierSelectionGridConfig({ onRowDelete: handleRowDelete });

  // enforce initial selection state and avoid error coloring
  // temporary fix until proper table validation styling is in place
  useEffect(() => {
    setRowSelectionModel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotedSupplier]);

  const rowSelectionModel = currentSelectedRowId ? [currentSelectedRowId] : [];

  return (
    <ChatStep
      title={tIntake('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={t('message')}
      footer={
        <Button
          disabled={disabled || isLoading || !hasSupplier}
          color="primary"
          variant="contained"
          onClick={handleNext}
        >
          {t('button.next')}
        </Button>
      }
    >
      {!isLoading && (
        <>
          <GridRowClickAwayListener onClickAway={handleCloseDrawer}>
            <IntakeDataGrid
              {...config}
              apiRef={apiRef}
              rows={quotedSupplier}
              initialState={{
                pinnedColumns: { right: [IntakeRequestSupplierSelectionGridField.DELETE_ACTION] },
              }}
              editMode="row"
              columns={config.columns}
              onRowSelectionModelChange={setRowSelectionModel}
              rowSelectionModel={rowSelectionModel}
              getRowClassName={({ id, row }) => {
                return id !== currentSelectedRowId || validate ? getRowClassName(row) : '';
              }}
              className={getGridClassName()}
            />

            <IntakeDrawer isOpen={!!selectedSupplier} handleCloseDrawer={handleCloseDrawer}>
              <IntakeDrawerTitle title={t('editSupplier')} handleCloseDrawer={handleCloseDrawer} />
              <IntakeDrawerContent>
                {selectedSupplier && (
                  <SupplierContactFormRoot
                    supplier={selectedSupplier}
                    suppliersOmit={otherSupplierIds}
                    onChange={handleSupplierChange}
                    validate={validate}
                    schema={confirmationItemSchema}
                  />
                )}
              </IntakeDrawerContent>
            </IntakeDrawer>
          </GridRowClickAwayListener>
          <Box
            sx={{
              mt: 3,
            }}
          >
            <Button
              disabled={hasSupplier}
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                handleAddRow();
              }}
              startIcon={<Add />}
            >
              {t('button.addSupplier')}
            </Button>
          </Box>
        </>
      )}
    </ChatStep>
  );
};
